import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { restorePlayPurchases } from "../lib/playBilling";
import api from "../lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = logged out, obj = user
  const [households, setHouseholds] = useState([]);
  const [activeId, setActiveId] = useState(localStorage.getItem("sorted_active_hh") || null);

  const loadHouseholds = useCallback(async () => {
    const { data } = await api.get("/households");
    setHouseholds(data);
    if (data.length) {
      const stored = localStorage.getItem("sorted_active_hh");
      const valid = data.find((h) => h.id === stored);
      const next = valid ? stored : data[0].id;
      setActiveId(next);
      localStorage.setItem("sorted_active_hh", next);
    } else {
      setActiveId(null);
      localStorage.removeItem("sorted_active_hh");
    }
    return data;
  }, []);

  const bootstrap = useCallback(async () => {
    // If returning from Google OAuth, let AuthCallback handle the exchange first.
    if (window.location.hash?.includes("session_id=")) return;
    const token = localStorage.getItem("sorted_token");
    if (!token) { setUser(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      await loadHouseholds();
      setUser(data);
    } catch {
      localStorage.removeItem("sorted_token");
      setUser(false);
    }
  }, [loadHouseholds]);

  useEffect(() => { bootstrap(); }, [bootstrap]);
  useEffect(() => { if (user?.id) restorePlayPurchases(user.id); }, [user?.id]);

  const finishAuth = async (data) => {
    localStorage.setItem("sorted_token", data.token);
    await loadHouseholds();
    setUser({ id: data.id, email: data.email, name: data.name, role: data.role,
              picture: data.picture, ai_provider: data.ai_provider || "openai", ai_model: data.ai_model });
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    await finishAuth(data);
  };

  const register = async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    await finishAuth(data);
  };

  // Called by AuthCallback after exchanging the Google session_id.
  const completeGoogle = async (sessionId) => {
    const { data } = await api.post("/auth/google/session", { session_id: sessionId });
    await finishAuth(data);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("sorted_token");
    localStorage.removeItem("sorted_active_hh");
    setUser(false);
    setHouseholds([]);
    setActiveId(null);
  };

  const selectHousehold = (id) => {
    setActiveId(id);
    localStorage.setItem("sorted_active_hh", id);
  };

  const updateSettings = async (ai_provider, ai_model) => {
    const { data } = await api.put("/settings", { ai_provider, ai_model });
    setUser((u) => ({ ...u, ai_provider: data.ai_provider, ai_model: data.ai_model }));
    return data;
  };

  const activeHousehold = households.find((h) => h.id === activeId) || null;

  return (
    <AuthContext.Provider
      value={{ user, households, activeId, activeHousehold, login, register, completeGoogle, setSession: finishAuth,
               logout, loadHouseholds, selectHousehold, setActiveId: selectHousehold, updateSettings, setUser,
               refreshUser: async () => { try { const { data } = await api.get("/auth/me"); setUser((u) => ({ ...u, ...data })); } catch {} } }}
    >
      {children}
    </AuthContext.Provider>
  );
}
